'use client';

import React, { useState } from 'react';
import { ChevronDown, Minus, Plus } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { safeSanitize } from '@/core/utils/sanitize';
import { useLocalizeHtml } from '@/core/i18n/navigation';
import { BlockProps } from './types';

// Answers written in the rich-text editor are HTML; older ones are plain text (line breaks kept).
const ANSWER_HTML_CLASSES = '[&_p]:mb-3 [&_p:last-child]:mb-0 [&_h2]:font-bold [&_h2]:text-[1.15em] [&_h2]:mt-5 [&_h2]:mb-2 [&_h3]:font-bold [&_h3]:mt-4 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:underline';

function FaqAnswer({ answer, className, localizeHtml }: { answer: string; className: string; localizeHtml: (html: string) => string }) {
    if (/<[a-z][\s\S]*>/i.test(answer)) {
        return <div className={cn(className, ANSWER_HTML_CLASSES)} dangerouslySetInnerHTML={{ __html: localizeHtml(safeSanitize(answer)) }} />;
    }
    return <p className={cn(className, 'whitespace-pre-line')}>{answer}</p>;
}

export interface FaqQuestion {
    question: string;
    answer: string;
}

export function FaqBlock({ block, currentStyles, isAdmin }: BlockProps) {
    const settings = block.settings || {};
    const localizeHtml = useLocalizeHtml();
    // questions and title live in settings (written by FaqEditor via updateBlockSettings)
    const questions: FaqQuestion[] = settings.questions || block.questions || [];

    // Resolve styles from currentStyles (merges desktop + viewport-specific overrides)
    const questionFontSize = currentStyles.questionFontSize ? `${currentStyles.questionFontSize}px` : '1rem';
    const answerFontSize = currentStyles.answerFontSize ? `${currentStyles.answerFontSize}px` : '0.9375rem';
    const questionFontWeight = currentStyles.questionFontWeight || '600';
    const answerFontWeight = currentStyles.answerFontWeight || '400';
    const questionColor = currentStyles.questionColor || '#0f172a';
    const answerColor = currentStyles.answerColor || '#475569';
    // Deliberately its own key (not the generic `borderColor`) — that one
    // colors this block's own outer wrapper border (via AppearanceEditor /
    // computeWrapperStyles), a different element than each FAQ item's border.
    const borderColor = currentStyles.itemBorderColor || '#e2e8f0';
    const bgColor = currentStyles.bgColor || '#ffffff';
    const activeBgColor = currentStyles.activeBgColor || '#f8fafc';
    const iconColor = currentStyles.iconColor || '#94a3b8';
    const gapVal = currentStyles.gap !== undefined ? currentStyles.gap : settings.gap;
    const gap = gapVal !== undefined ? `${gapVal}px` : '8px';
    const borderRadiusVal = currentStyles.borderRadius !== undefined ? currentStyles.borderRadius : settings.borderRadius;
    const borderRadius = borderRadiusVal !== undefined ? `${borderRadiusVal}px` : '12px';
    // Lets a merchant combine "Pełna szerokość" (section bleeds edge-to-edge, e.g. for a
    // colored background) with a readable, centered column for the actual questions —
    // undefined by default so existing pages that never touch this keep rendering exactly
    // as before (bounded by the page's normal content width via BlockRenderer instead).
    const contentMaxWidthVal = currentStyles.contentMaxWidth ?? settings.contentMaxWidth;
    const contentMaxWidth = contentMaxWidthVal ? `${contentMaxWidthVal}px` : undefined;
    const titleText = settings.title || block.title || '';
    const titleFontSize = currentStyles.titleFontSize ? `${currentStyles.titleFontSize}px` : '1.5rem';
    const titleFontWeight = currentStyles.titleFontWeight || '700';
    const titleColor = currentStyles.titleColor || '#0f172a';
    const titleAlign = currentStyles.titleAlign || 'left';

    const blockClass = `faq-${block.id}`;

    const DEMO_QUESTIONS: FaqQuestion[] = [
        { question: 'Jak mogę złożyć zamówienie?', answer: 'Wybierz produkt, dodaj do koszyka i przejdź do kasy. Akceptujemy wszystkie popularne metody płatności.' },
        { question: 'Jaki jest czas dostawy?', answer: 'Standardowa dostawa trwa 2-3 dni robocze. Ekspresowa dostawa jest dostępna następnego dnia roboczego.' },
        { question: 'Czy mogę zwrócić zakupiony towar?', answer: 'Tak, masz 14 dni na zwrot towaru bez podawania przyczyny. Skontaktuj się z nami przez formularz kontaktowy.' },
    ];

    const displayQuestions = questions.length > 0 ? questions : (isAdmin ? DEMO_QUESTIONS : []);

    if (displayQuestions.length === 0 && !isAdmin) return null;

    return (
        <div className={cn('w-full', blockClass)}>
            <style>{`
                .${blockClass} .faq-item {
                    background: ${bgColor};
                    border: 1px solid ${borderColor};
                    border-radius: ${borderRadius};
                    overflow: hidden;
                    transition: box-shadow 200ms, border-color 200ms;
                }
                .${blockClass} .faq-item:hover {
                    border-color: ${iconColor};
                }
                .${blockClass} .faq-question {
                    font-size: ${questionFontSize};
                    font-weight: ${questionFontWeight};
                    color: ${questionColor};
                }
                .${blockClass} .faq-answer {
                    font-size: ${answerFontSize};
                    font-weight: ${answerFontWeight};
                    color: ${answerColor};
                    line-height: 1.7;
                }
                .${blockClass} .faq-icon {
                    color: ${iconColor};
                    flex-shrink: 0;
                }
                .${blockClass} .faq-answer-wrapper {
                    border-top: 1px solid ${borderColor};
                    background: ${activeBgColor};
                }
                .${blockClass} .faq-title {
                    font-size: ${titleFontSize};
                    font-weight: ${titleFontWeight};
                    color: ${titleColor};
                    text-align: ${titleAlign};
                }
            `}</style>

            <div style={{ maxWidth: contentMaxWidth, marginLeft: contentMaxWidth ? 'auto' : undefined, marginRight: contentMaxWidth ? 'auto' : undefined }}>

            {titleText && (
                <div className="faq-title mb-6">{titleText}</div>
            )}

            {isAdmin && questions.length === 0 && (
                <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-[11px] text-blue-600 font-semibold">
                    Podgląd demo — dodaj pytania w panelu bocznym
                </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap }}>
                {displayQuestions.map((item, i) => {
                    if (settings.layout === 'list') {
                        const isLast = i === displayQuestions.length - 1;
                        const separatorWidth = currentStyles.listSeparatorWidth ?? settings.listSeparatorWidth ?? 1;
                        const separatorColor = settings.listSeparatorColor || '#e5e5e5';
                        const separatorStyle = settings.listSeparatorStyle || 'solid';
                        return (
                            <div
                                key={i}
                                className="py-6 text-left"
                                style={!isLast ? { borderBottom: `${separatorWidth}px ${separatorStyle} ${separatorColor}` } : undefined}
                            >
                                <h3 className="faq-question font-display font-extrabold text-neutral-900 text-lg sm:text-xl">{item.question}</h3>
                                <FaqAnswer answer={item.answer} className="faq-answer mt-2 leading-relaxed text-neutral-600" localizeHtml={localizeHtml} />
                            </div>
                        );
                    }
                    return (
                        <FaqItem
                            key={i}
                            question={item.question}
                            answer={item.answer}
                            blockClass={blockClass}
                            iconStyle={settings.iconStyle === 'plus' ? 'plus' : 'chevron'}
                            defaultOpen={settings.openFirst === true && i === 0}
                            localizeHtml={localizeHtml}
                        />
                    );
                })}
            </div>
            </div>
        </div>
    );
}

function FaqItem({ question, answer, iconStyle, defaultOpen, localizeHtml }: {
    question: string;
    answer: string;
    blockClass: string;
    iconStyle: 'chevron' | 'plus';
    defaultOpen: boolean;
    localizeHtml: (html: string) => string;
}) {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className="faq-item">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                aria-expanded={open}
            >
                <span className="faq-question">{question}</span>
                {iconStyle === 'plus' ? (
                    open ? <Minus className="faq-icon w-4 h-4" /> : <Plus className="faq-icon w-4 h-4" />
                ) : (
                    <ChevronDown className={cn('faq-icon w-5 h-5 transition-transform duration-300', open && 'rotate-180')} />
                )}
            </button>
            <div
                className={cn(
                    'grid transition-all duration-300 ease-in-out',
                    open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                )}
            >
                <div className="overflow-hidden">
                    <div className="faq-answer-wrapper px-5 py-4">
                        <FaqAnswer answer={answer} className="faq-answer" localizeHtml={localizeHtml} />
                    </div>
                </div>
            </div>
        </div>
    );
}
